import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, 
  Upload, 
  Globe, 
  Loader2, 
  ArrowRight, 
  CheckCircle2, 
  FileSearch,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import axios from '@/lib/axios';
import { useJourney } from '@/contexts/JourneyContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

import BankStatementUpload from './BankStatementUpload';
import NetBankingFetch from './NetBankingFetch';

type Method = 'UPLOAD' | 'NETBANKING';

interface Bank {
  code: string;
  name: string;
}

interface BankStatementCollectionProps {
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  /** When true (builder/preview mode) skip all API calls and show a static placeholder */
  isBuilderMode?: boolean;
}

const BankStatementCollection: React.FC<BankStatementCollectionProps> = ({ value, onChange, disabled, registerSubmit, isBuilderMode = false }) => {
  const { logEvent } = useJourney();
  const [activeMethod, setActiveMethod] = useState<Method>('UPLOAD');
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [analysisStatus, setAnalysisStatus] = useState<'none' | 'pending' | 'completed' | 'error'>(value?.status || 'none');
  const [requestId, setRequestId] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const uploadRef = React.useRef<any>(null);

  useEffect(() => {
    if (registerSubmit) {
      registerSubmit(async () => {
        // If already completed, allow advancement immediately
        if (analysisStatus === 'completed') return true;
        
        if (activeMethod === 'UPLOAD' && uploadRef.current) {
           return await uploadRef.current.triggerUpload();
        }
        
        // For NetBanking, we assume it's either pending or will be handled by its own logic
        return true; 
      });
    }
  }, [registerSubmit, activeMethod, analysisStatus]);

  const fetchConfig = async () => {
    try {
      const response = await axios.post('/api/los/orchestrator/', {
        components: ['LOS_GetCartConfig_020']
      });
      const data = response.data['LOS_GetCartConfig_020'];
      setBanks(data.banks || []);
      setAnalysisStatus(data.submission_status || 'none');
      setRequestId(data.last_request_id || null);
      
      // If pending, resume with correct mode
      if (data.submission_status === 'pending' && data.last_request_id) {
        startPolling(data.last_request_id, data.is_netbanking);
      }
    } catch (error) {
      console.error('Failed to fetch CART config:', error);
      toast.error('Failed to initialize bank statement service');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isBuilderMode) fetchConfig();
  }, [isBuilderMode]);

  const checkStatus = useCallback(async (id: string, isNetbanking: boolean = true) => {
    try {
      const response = await axios.post('/api/los/orchestrator/', {
        components: ['LOS_CheckAnalysisStatus_023'],
        LOS_CheckAnalysisStatus_023: {
          request_id: id,
          is_netbanking: isNetbanking
        }
      });
      
      const result = response.data['LOS_CheckAnalysisStatus_023'];
      
      if (result.status === 'completed') {
        setAnalysisStatus('completed');
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        toast.success('Analysis complete!');
        
        if (onChange) {
          onChange({ status: 'completed', request_id: id });
        } else {
          // Legacy standalone behaviour
          setTimeout(async () => {
             const res = await logEvent({ advance: true, route: window.location.pathname });
             if (res?.next_page_url) window.location.href = res.next_page_url;
          }, 2000);
        }
      } else if (result.status === 'error') {
        setAnalysisStatus('error');
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        toast.error('Analysis failed. Please try again.');
      }
    } catch (error) {
      console.error('Status check error:', error);
    }
  }, [logEvent, onChange]);

  const startPolling = (id: string, isNetbanking: boolean = true) => {
    setRequestId(id);
    setAnalysisStatus('pending');
    
    // Poll every 5 seconds
    if (intervalRef.current) clearInterval(intervalRef.current);
    
    intervalRef.current = setInterval(() => {
      checkStatus(id, isNetbanking);
    }, 5000);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);


  // Builder/preview mode: static placeholder — no API calls
  if (isBuilderMode) {
    return (
      <div className="space-y-3 p-2">
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
          <div className="text-2xl mb-1">🏛️</div>
          <p className="font-semibold text-gray-500">Bank Statement Collection</p>
          <p className="text-xs mt-1">PDF upload / NetBanking fetch UI will appear here during the journey.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="h-10 w-10 text-intellend-green animate-spin" />
        <p className="text-gray-500 font-medium animate-pulse">Setting up secure vault...</p>
      </div>
    );
  }

  // Polling / Loading State UI
  if (analysisStatus === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] text-center p-8 space-y-8 animate-in fade-in zoom-in duration-500">
        <div className="relative">
          <div className="absolute inset-0 bg-emerald-100 rounded-full blur-2xl opacity-50 scale-150 animate-pulse" />
          <div className="relative h-32 w-32 bg-white rounded-[40px] shadow-2xl flex items-center justify-center border-4 border-emerald-50">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
              className="absolute inset-0"
            >
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 bg-emerald-500 rounded-full" />
            </motion.div>
            <FileSearch size={48} className="text-emerald-600" />
          </div>
        </div>
        
        <div className="space-y-3">
          <h2 className="text-2xl font-black text-gray-900 leading-tight">Analyzing Statements</h2>
          <p className="text-gray-500 text-sm max-w-xs mx-auto leading-relaxed">
            Our AI is securely processing your bank statements to assess your credit health. This usually takes 1-2 minutes.
          </p>
        </div>

        <div className="flex flex-col items-center gap-4 w-full max-w-[240px]">
          <div className="flex items-center gap-2 text-xs font-bold text-emerald-600 uppercase tracking-widest bg-emerald-50 px-4 py-2 rounded-full border border-emerald-100">
            <RefreshCw size={12} className="animate-spin" />
            Live Status: In Progress
          </div>
          <button 
            onClick={() => checkStatus(requestId!, activeMethod === 'NETBANKING')}
            className="text-[10px] items-center gap-1 font-bold text-gray-400 hover:text-gray-600 transition-colors uppercase tracking-tighter flex"
          >
            Check status now
          </button>
        </div>

        <p className="text-[10px] text-gray-400 italic">Please do not close this window</p>
      </div>
    );
  }

  if (analysisStatus === 'completed') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] text-center p-8 space-y-6 animate-in fade-in duration-700">
        <div className="h-24 w-24 bg-emerald-500 rounded-full flex items-center justify-center shadow-emerald-200 shadow-2xl">
          <CheckCircle2 size={48} className="text-white" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-black text-gray-900">Success!</h2>
          <p className="text-gray-500 font-medium">Your financial profile has been verified.</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => {
              setAnalysisStatus('none');
              setRequestId(null);
            }} 
            className="h-14 px-8 rounded-2xl border-2 font-bold text-gray-500"
          >
            Re-upload
          </Button>
          {!onChange ? (
            <Button onClick={async () => {
              const res = await logEvent({ advance: true, route: window.location.pathname });
              if (res?.next_page_url) window.location.href = res.next_page_url;
            }} className="h-14 px-10 rounded-2xl bg-black text-white hover:bg-zinc-800 font-bold flex gap-2">
               Continue Journey <ArrowRight size={18} />
            </Button>
          ) : (
             <p className="text-sm text-gray-400 font-medium italic">Ready to proceed</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto space-y-8 py-4 px-2">
      {/* Header Section */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-zinc-100 rounded-full text-[10px] font-black uppercase tracking-widest text-zinc-500">
          <Building2 size={12} />
          Financial Verification
        </div>
        <h1 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight px-2">
          How would you like to share your statements?
        </h1>
        <p className="text-gray-500 text-sm font-medium">
          Verify your credit limit with your last 6 months' records.
        </p>
      </div>

      {/* Modern Method Toggle */}
      <div className="w-full">
        <div className="p-1.5 bg-gray-100/80 backdrop-blur-sm rounded-[32px] grid grid-cols-2 shadow-inner">
          <button
            onClick={() => setActiveMethod('UPLOAD')}
            className={`flex items-center justify-center gap-2 py-4 rounded-[26px] transition-all duration-500 font-bold text-sm
              ${activeMethod === 'UPLOAD' 
                ? 'bg-white text-gray-900 shadow-xl scale-[1.02]' 
                : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Upload size={18} className={activeMethod === 'UPLOAD' ? 'text-emerald-500' : 'text-gray-400'} />
            PDF Upload
          </button>
          <button
            onClick={() => setActiveMethod('NETBANKING')}
            className={`flex items-center justify-center gap-2 py-4 rounded-[26px] transition-all duration-500 font-bold text-sm
              ${activeMethod === 'NETBANKING' 
                ? 'bg-white text-gray-900 shadow-xl scale-[1.02]' 
                : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Globe size={18} className={activeMethod === 'NETBANKING' ? 'text-amber-500' : 'text-gray-400'} />
            Auto-Fetch
          </button>
        </div>
      </div>

      {/* Step Content */}
      <div className="w-full">
        <div className="bg-white rounded-[32px] md:rounded-[40px] border border-gray-100 shadow-xl relative overflow-hidden">
          <div className="p-2 md:p-4 relative z-10 w-full flex flex-col items-center">

            <AnimatePresence mode="wait">
              {activeMethod === 'UPLOAD' ? (
                <motion.div
                  key="upload"
                  className="w-full"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <BankStatementUpload 
                    ref={uploadRef}
                    banks={banks} 
                    onSuccess={(id) => startPolling(id, false)} 
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="netbanking"
                  className="w-full"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <NetBankingFetch 
                    banks={banks} 
                    onInitiated={(id) => startPolling(id, true)} 
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>


      {/* Error state if analysis failed previously */}
      {analysisStatus === 'error' && (
        <div className="mx-4 p-4 rounded-3xl bg-red-50 border border-red-100 flex items-center gap-4 animate-bounce">
          <AlertCircle className="text-red-500 shrink-0" size={24} />
          <p className="text-xs text-red-700 font-semibold leading-relaxed">
            There was an issue processing your previous statement. Please try re-uploading your official bank statement PDF.
          </p>
        </div>
      )}
    </div>
  );
};

export default BankStatementCollection;
